import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";
import { Form } from "react-bootstrap";
import { searchSavRattachement } from "state/sav/actions";

/**
 * Autosuggest commande groupée (M/) ou prestation (P) pour rattacher un SAV.
 *
 * value shape:
 * { type, grouped_order_id, prestation_id, label, order_number, ... }
 */
const SavRattachementSelect = ({ value, onChange, placeholder, isDisabled }) => {
  const dispatch = useDispatch();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const debounceRef = useRef(null);

  const toOption = (row) => ({
    value: row.type === "prestation" ? `p-${row.prestation_id}` : `g-${row.grouped_order_id}`,
    label: row.label,
    data: row,
  });

  const selectedOption = value
    ? {
        value: value.prestation_id ? `p-${value.prestation_id}` : `g-${value.grouped_order_id}`,
        label: value.label || value.order_number,
        data: value,
      }
    : null;

  const fetchOptions = useCallback(
    async (q) => {
      if (!q || q.length < 2) {
        setOptions([]);
        return;
      }
      setLoading(true);
      const resp = await dispatch(searchSavRattachement(q));
      setLoading(false);
      setOptions((resp?.results || []).map(toOption));
    },
    [dispatch]
  );

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      fetchOptions(inputValue);
    }, 300);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [inputValue, fetchOptions]);

  const handleChange = (opt) => {
    onChange(opt ? opt.data : null);
  };

  const formatOptionLabel = (opt) => (
    <div>
      <div>{opt.label}</div>
      {opt.data?.type === "prestation" && (
        <small className="text-muted">Prestation</small>
      )}
      {opt.data?.type === "grouped" && (
        <small className="text-muted">Commande groupée (devis entier)</small>
      )}
    </div>
  );

  return (
    <Form.Group>
      <Select
        classNamePrefix="sav-rattachement"
        placeholder={placeholder || "Rechercher M/6173, client, entreprise, P35…"}
        isClearable
        isDisabled={isDisabled}
        isLoading={loading}
        filterOption={() => true}
        options={options}
        value={selectedOption}
        onChange={handleChange}
        onInputChange={(v) => setInputValue(v)}
        formatOptionLabel={formatOptionLabel}
        noOptionsMessage={() =>
          inputValue.length < 2 ? "Tapez au moins 2 caractères" : "Aucun résultat"
        }
        loadingMessage={() => "Recherche…"}
      />
    </Form.Group>
  );
};

/** Construit le payload API à partir d'une option sélectionnée */
export const rattachementToPayload = (selection) => {
  if (!selection) {
    return {};
  }
  if (selection.type === "prestation" && selection.prestation_id) {
    return { prestation_id: selection.prestation_id };
  }
  if (selection.grouped_order_id) {
    return {
      commande_groupee_id: selection.grouped_order_id,
      order_number: selection.order_number || `M/${selection.grouped_order_id}`,
    };
  }
  return {};
};

export default SavRattachementSelect;
